/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import type { FunctionComponent, JSX } from "react";
import { useCallback, useEffect, useMemo } from "react";

import {
  useUnityDispatch,
  useUnitySelector,
  UnityAppStore
} from "../../../viewModel";
import {
  setFocusedActionIndex,
  selectIsUnityDialogVisible,
  selectUnityDialogTitle,
  selectUnityDialogMessage,
  selectUnityDialogErrorCode,
  selectUnityDialogVariant,
  selectUnityDialogAutoHide,
  selectUnityDialogAutoHideTimeoutMs,
  selectUnityDialogOnAutoHideActionId,
  selectUnityDialogActions,
  selectUnityDialogFocusedActionIndex,
  triggerUnityDialogAction,
  dismissUnityDialogWithoutAction
} from "../../../viewModel/unityDialog";
import { useModalController } from "../../hooks/useModalController";
import { UnityDialogController } from "./UnityDialogController";
import UnityDialog from "./UnityDialog";

/**
 * Default auto-dismiss timeout (ms) for dialogs opened with `autoHide: true`.
 * A dialog may override it via `autoHideTimeoutMs`. Lives here (not in the pure
 * presentational `UnityDialog.tsx`) since starting/clearing the timer is a
 * side effect that requires `dispatch`.
 */
export const AUTO_HIDE_TIMEOUT_MS: number = 60000;

/**
 * Connected container that reads UnityDialog state from Redux and renders
 * the UnityDialog when visible.
 *
 * A SINGLE instance of this container is mounted globally in App.tsx — any
 * feature can trigger it from anywhere via the `showUnityDialog` thunk.
 *
 * Pushes a UnityDialogController onto the active screen's modal stack so all
 * D-pad input is consumed by the dialog while it is open.
 */
const UnityDialogContainer: FunctionComponent = (): JSX.Element | null => {
  const dispatch = useUnityDispatch();

  const isVisible = useUnitySelector(selectIsUnityDialogVisible);
  const title = useUnitySelector(selectUnityDialogTitle);
  const message = useUnitySelector(selectUnityDialogMessage);
  const errorCode = useUnitySelector(selectUnityDialogErrorCode);
  const variant = useUnitySelector(selectUnityDialogVariant);
  const autoHide = useUnitySelector(selectUnityDialogAutoHide);
  const autoHideTimeoutMs = useUnitySelector(
    selectUnityDialogAutoHideTimeoutMs
  );
  const onAutoHideActionId = useUnitySelector(
    selectUnityDialogOnAutoHideActionId
  );
  const actions = useUnitySelector(selectUnityDialogActions);
  const focusedActionIndex = useUnitySelector(
    selectUnityDialogFocusedActionIndex
  );

  const modalController = useMemo(
    (): UnityDialogController =>
      new UnityDialogController(dispatch, UnityAppStore.getState),
    [dispatch]
  );

  useModalController(modalController, isVisible);

  // Auto-dismiss after a timeout when the dialog was opened with
  // `autoHide: true`. Timer is cleared on unmount, on dialog change, and
  // whenever any of its inputs flip, so a leftover timer can never fire
  // against a later, unrelated dialog.
  //
  // By default this is a plain dismiss that activates no action. When the
  // dialog supplied `onAutoHideActionId`, that action is activated instead so
  // its registered `onPress` runs — this is how a playback error dialog can
  // leave the player on timeout (kaveri's `onAutoHide → showPreviousScreen()`)
  // without putting a callback in Redux.
  useEffect((): (() => void) | undefined => {
    if (isVisible === true && autoHide === true) {
      const timeoutMs: number =
        autoHideTimeoutMs > 0 ? autoHideTimeoutMs : AUTO_HIDE_TIMEOUT_MS;
      const timerId = setTimeout((): void => {
        if (onAutoHideActionId !== "") {
          triggerUnityDialogAction(dispatch, onAutoHideActionId);
          return;
        }
        dismissUnityDialogWithoutAction(dispatch);
      }, timeoutMs);
      return (): void => clearTimeout(timerId);
    }
    return undefined;
  }, [isVisible, autoHide, autoHideTimeoutMs, onAutoHideActionId, dispatch]);

  const handleAction = useCallback(
    (actionId: string): void => {
      triggerUnityDialogAction(dispatch, actionId);
    },
    [dispatch]
  );

  const handleFocusAction = useCallback(
    (index: number): void => {
      dispatch(setFocusedActionIndex(index));
    },
    [dispatch]
  );

  if (isVisible !== true) {
    return null;
  }

  return (
    <UnityDialog
      title={title}
      message={message}
      errorCode={errorCode}
      variant={variant}
      actions={actions}
      focusedActionIndex={focusedActionIndex}
      onAction={handleAction}
      onFocusAction={handleFocusAction}
    />
  );
};

export default UnityDialogContainer;
